import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, Users, Trophy, FileText, DollarSign } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { useApiClient } from '../services/api';
import { Bounty, Hackday as HackdayType } from '../types/context';
import BackButton from '../components/BackButton';

const HackdayDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userRole } = useAppContext();
  const apiClient = useApiClient();
  const [hackday, setHackday] = useState<HackdayType | null>(null);
  const [bounties, setBounties] = useState<Bounty[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegistered, setIsRegistered] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load hackday and bounties data
  useEffect(() => {
    const loadHackdayData = async () => {
      if (!id) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Load hackday details
        const hackdayResponse = await apiClient.getHackday(id);
        if (!hackdayResponse.success) {
          throw new Error(hackdayResponse.message || 'Failed to load hackday');
        }
        
        // Convert date strings to Date objects
        const hackdayData = {
          ...hackdayResponse.data!,
          startDate: new Date(hackdayResponse.data!.startDate),
          endDate: new Date(hackdayResponse.data!.endDate),
          registrationDeadline: new Date(hackdayResponse.data!.registrationDeadline),
          createdAt: new Date(hackdayResponse.data!.createdAt),
          updatedAt: new Date(hackdayResponse.data!.updatedAt),
        };
        
        setHackday(hackdayData);
        
        // Load bounties
        const bountiesResponse = await apiClient.getHackdayBounties(id);
        if (bountiesResponse.success && bountiesResponse.data) {
          const bountiesWithDates = bountiesResponse.data.map(bounty => ({
            ...bounty,
            startDate: bounty.startDate ? new Date(bounty.startDate) : undefined,
            endDate: bounty.endDate ? new Date(bounty.endDate) : undefined,
            createdAt: new Date(bounty.createdAt),
            updatedAt: new Date(bounty.updatedAt),
          }));
          setBounties(bountiesWithDates);
        }
        
        // Check registration status for participants
        if (userRole === 'participant') {
          const statusResponse = await apiClient.checkUserTeamForEvent(id);
          if (statusResponse.success && statusResponse.data) {
            setIsRegistered(statusResponse.data.hasTeam);
          }
        }
      } catch (error) {
        console.error('Error loading hackday data:', error);
        setError(error instanceof Error ? error.message : 'Failed to load hackday data');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadHackdayData();
  }, [id, apiClient, userRole]);

  const handleRegister = () => {
    if (!id) return;
    
    // Redirect to team registration page (registration requires team setup)
    navigate(`/team-registration/${id}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
          <p className="text-primary-text font-space">Loading hackday details...</p>
        </div>
      </div>
    );
  }

  if (!hackday) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold font-space text-primary-text mb-4">
            Hackday Not Found
          </h1>
          <p className="text-primary-text font-space mb-6">
            The hackday you're looking for doesn't exist or may have been removed.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="bg-accent hover:bg-yellow-400 text-primary-text px-6 py-3 rounded-lg font-space font-semibold transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const now = new Date();
  const startDate = new Date(hackday.startDate);
  const endDate = new Date(hackday.endDate);
  const registrationDeadline = new Date(hackday.registrationDeadline);

  const isUpcoming = startDate > now;
  const isOngoing = startDate <= now && endDate >= now;
  const isCompleted = endDate < now;
  const canRegister = now < registrationDeadline && hackday.currentParticipants < hackday.maxParticipants;

  const getDaysUntil = (date: Date) => {
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="min-h-screen bg-transparent py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <BackButton className="mb-6" />

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="text-red-800 font-space">
                <p className="font-medium">Error</p>
                <p className="text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="bg-transparent rounded-lg border border-subtle p-8 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-4">
                <span className="bg-accent text-primary-text px-4 py-2 rounded-full text-sm font-space font-medium">
                  {hackday.theme}
                </span>
                <span className={`px-4 py-2 rounded-full text-sm font-space font-medium capitalize ${
                  isUpcoming ? 'bg-blue-100 text-blue-800' :
                  isOngoing ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {isUpcoming ? 'Upcoming' : isOngoing ? 'Ongoing' : 'Completed'}
                </span>
              </div>

              <h1 className="text-3xl md:text-4xl font-bold font-space text-primary-text mb-4">
                {hackday.title}
              </h1>

              <p className="text-lg text-primary-text font-space mb-6">
                {hackday.description}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-5 w-5 text-accent" />
                  <div className="font-space">
                    <p className="text-sm text-gray-600">Start Date</p>
                    <p className="font-semibold text-primary-text">{startDate.toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="h-5 w-5 text-accent" />
                  <div className="font-space">
                    <p className="text-sm text-gray-600">Duration</p>
                    <p className="font-semibold text-primary-text">
                      {Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))} days
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="h-5 w-5 text-accent" />
                  <div className="font-space">
                    <p className="text-sm text-gray-600">Participants</p>
                    <p className="font-semibold text-primary-text">
                      {hackday.currentParticipants}/{hackday.maxParticipants}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Trophy className="h-5 w-5 text-accent" />
                      <div className="font-space">
                        <p className="text-sm text-gray-600">Total Bounties</p>
                        <p className="font-semibold text-primary-text">
                          ${bounties.reduce((sum, b) => sum + b.value, 0).toLocaleString()}
                        </p>
                      </div>
                </div>
              </div>
            </div>

            {/* Registration CTA */}
            {userRole === 'participant' && (
              <div className="mt-6 lg:mt-0 lg:ml-8">
                <div className="bg-subtle p-6 rounded-lg text-center min-w-[280px]">
                  {isUpcoming && canRegister && !isRegistered && (
                    <>
                      <h3 className="text-lg font-bold font-space text-primary-text mb-2">
                        Registration Open
                      </h3>
                      <p className="text-sm text-gray-600 font-space mb-4">
                        Deadline: {registrationDeadline.toLocaleDateString()}
                        {getDaysUntil(registrationDeadline) > 0 && (
                          <span className="block">({getDaysUntil(registrationDeadline)} days left)</span>
                        )}
                      </p>
                      <button
                        onClick={handleRegister}
                        className="w-full bg-accent hover:bg-yellow-400 text-primary-text py-3 px-6 rounded-lg font-space font-semibold transition-colors"
                      >
                        Register Now
                      </button>
                    </>
                  )}
                  {isRegistered && (
                    <>
                      <h3 className="text-lg font-bold font-space text-primary-text mb-2">
                        Registration Confirmed
                      </h3>
                      <p className="text-sm text-gray-600 font-space">
                        You are registered for this hackday!
                      </p>
                    </>
                  )}
                  {isUpcoming && !canRegister && (
                    <>
                      <h3 className="text-lg font-bold font-space text-primary-text mb-2">
                        Registration Closed
                      </h3>
                      <p className="text-sm text-gray-600 font-space">
                        {hackday.currentParticipants >= hackday.maxParticipants 
                          ? 'Maximum participants reached'
                          : 'Registration deadline has passed'
                        }
                      </p>
                    </>
                  )}
                  {isOngoing && (
                    <>
                      <h3 className="text-lg font-bold font-space text-primary-text mb-2">
                        Event In Progress
                      </h3>
                      <p className="text-sm text-gray-600 font-space">
                        Ends in {getDaysUntil(endDate)} days
                      </p>
                    </>
                  )}
                  {isCompleted && (
                    <>
                      <h3 className="text-lg font-bold font-space text-primary-text mb-2">
                        Event Completed
                      </h3>
                      <p className="text-sm text-gray-600 font-space">
                        Thank you to all participants!
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Rules and Guidelines */}
            <div className="bg-transparent rounded-lg border border-subtle p-6">
              <h2 className="text-2xl font-bold font-space text-primary-text mb-4 flex items-center">
                <FileText className="h-6 w-6 mr-2 text-accent" />
                Rules and Guidelines
              </h2>
              <div className="prose prose-sm max-w-none font-space">
                <p className="text-primary-text whitespace-pre-line">{hackday.rules}</p>
              </div>
            </div>

            {/* Bounties */}
            {bounties.length > 0 && (
              <div className="bg-transparent rounded-lg border border-subtle p-6">
                <h2 className="text-2xl font-bold font-space text-primary-text mb-6 flex items-center">
                  <DollarSign className="h-6 w-6 mr-2 text-accent" />
                  Bounties
                </h2>
                
                <div className="space-y-6">
                  {bounties.map((bounty) => (
                    <div key={bounty.id} className="border border-subtle rounded-lg p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-xl font-bold font-space text-primary-text">
                              {bounty.title}
                            </h3>
                            <span className={`px-3 py-1 rounded-full text-sm font-space font-medium ${
                              bounty.status === 'active' ? 'bg-green-100 text-green-800' :
                              bounty.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                              bounty.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {bounty.status}
                            </span>
                            <span className={`px-3 py-1 rounded-full text-sm font-space font-medium ${
                              bounty.difficulty === 'beginner' ? 'bg-green-100 text-green-800' :
                              bounty.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                              bounty.difficulty === 'advanced' ? 'bg-orange-100 text-orange-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {bounty.difficulty}
                            </span>
                          </div>
                          <p className="text-primary-text font-space mb-4">
                            {bounty.description}
                          </p>
                        </div>
                        <div className="text-right ml-6">
                          <div className="flex items-center text-2xl font-bold font-space text-primary-text">
                            <DollarSign className="h-6 w-6 text-accent" />
                            {bounty.value.toLocaleString()}
                          </div>
                          <p className="text-sm text-gray-600 font-space">{bounty.currency}</p>
                          <p className="text-xs text-gray-500 font-space">
                            {bounty.currentWinners}/{bounty.maxWinners} winners
                          </p>
                        </div>
                      </div>

                      {/* Requirements */}
                      {bounty.requirements.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-semibold font-space text-primary-text mb-2">Requirements:</h4>
                          <ul className="space-y-1">
                            {bounty.requirements.map((req, index) => (
                              <li key={index} className="flex items-center text-sm text-primary-text font-space">
                                <div className="w-2 h-2 bg-accent rounded-full mr-3 flex-shrink-0"></div>
                                {req}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Tags */}
                      {bounty.tags.length > 0 && (
                        <div className="mb-4">
                          <div className="flex flex-wrap gap-2">
                            {bounty.tags.map((tag, index) => (
                              <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded font-space">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Dates */}
                      {(bounty.startDate || bounty.endDate) && (
                        <div className="text-xs text-gray-500 font-space">
                          {bounty.startDate && (
                            <div>Available from: {bounty.startDate.toLocaleDateString()}</div>
                          )}
                          {bounty.endDate && (
                            <div>Expires: {bounty.endDate.toLocaleDateString()}</div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Event Timeline */}
            <div className="bg-transparent rounded-lg border border-subtle p-6">
              <h3 className="text-lg font-bold font-space text-primary-text mb-4">
                Event Timeline
              </h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${now >= registrationDeadline ? 'bg-green-500' : 'bg-accent'}`}></div>
                  <div className="font-space">
                    <p className="font-medium text-primary-text">Registration Deadline</p>
                    <p className="text-sm text-gray-600">{registrationDeadline.toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${now >= startDate ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <div className="font-space">
                    <p className="font-medium text-primary-text">Event Starts</p>
                    <p className="text-sm text-gray-600">{startDate.toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${now >= endDate ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <div className="font-space">
                    <p className="font-medium text-primary-text">Event Ends</p>
                    <p className="text-sm text-gray-600">{endDate.toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-transparent rounded-lg border border-subtle p-6">
              <h3 className="text-lg font-bold font-space text-primary-text mb-4">
                Quick Stats
              </h3>
              <div className="space-y-4">
                {bounties.length > 0 && (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="font-space text-gray-600">Total Bounty Value</span>
                      <span className="font-bold font-space text-primary-text">
                        ${bounties.reduce((sum, b) => sum + b.value, 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-space text-gray-600">Total Bounties</span>
                      <span className="font-bold font-space text-primary-text">
                        {bounties.length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-space text-gray-600">Active Bounties</span>
                      <span className="font-bold font-space text-primary-text">
                        {bounties.filter(b => b.status === 'active').length}
                      </span>
                    </div>
                  </>
                )}
                <div className="flex justify-between items-center">
                  <span className="font-space text-gray-600">Spots Remaining</span>
                  <span className="font-bold font-space text-primary-text">
                    {Math.max(0, hackday.maxParticipants - hackday.currentParticipants)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HackdayDetails;
